import {
  platformBrowser,
  BrowserModule,
  bootstrapApplication,
} from '@angular/platform-browser';

import * as Sentry from '@sentry/angular';
import { environment } from './environments/environment';
import {
  ErrorHandler,
  provideAppInitializer,
  importProvidersFrom,
  isDevMode,
  provideZoneChangeDetection,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { CacheInterceptor } from './app/core/_interceptors/cache.interceptor';
import { FormsModule } from '@angular/forms';
import { provideAnimations } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { provideServiceWorker } from '@angular/service-worker';
import { GlobalErrorHandler } from './app/core/_services/global-error-handler.service';

/**
 * Detects errors that originate from native-bridge scripts injected by Meta's
 * in-app browsers (Instagram/Facebook) rather than from our own code. These
 * surface as `window.webkit.messageHandlers` access failures inside
 * `sendDataToNative` / `sendPageHideMessage`, which we do not ship. Message
 * text varies by engine, so we also inspect the stack frame function names.
 */
const INJECTED_NATIVE_BRIDGE_PATTERNS = [
  'messageHandlers',
  'sendDataToNative',
  'sendPageHideMessage',
];

function isInjectedNativeBridgeError(event: Sentry.ErrorEvent): boolean {
  const values = event.exception?.values ?? [];

  return values.some((value) => {
    if (
      typeof value.value === 'string' &&
      INJECTED_NATIVE_BRIDGE_PATTERNS.some((p) => value.value!.includes(p))
    ) {
      return true;
    }

    const frames = value.stacktrace?.frames ?? [];
    return frames.some(
      (frame) =>
        typeof frame.function === 'string' &&
        INJECTED_NATIVE_BRIDGE_PATTERNS.some((p) => frame.function!.includes(p)),
    );
  });
}

if (environment.production) {
  Sentry.init({
    dsn: 'https://a3732c2273d491262779a8d518785b0c@o4509633562542080.ingest.us.sentry.io/4509633570209792',
    integrations: [
      // Sentry.browserTracingIntegration(),
      // Sentry.browserProfilingIntegration()
    ],
    sendDefaultPii: true,
    environment: environment.sentryEnvironment,
    // Drop noise from third-party scripts we don't control. Meta's in-app
    // browsers (Instagram/Facebook) inject their own JS into the page to bridge
    // to native iOS via `window.webkit.messageHandlers`. Their injected
    // sendPageHideMessage/sendDataToNative helpers throw when the WKWebView
    // bridge is absent or torn down on pagehide. Not our code. See ZMBZ-SITE-2B.
    ignoreErrors: [
      /window\.webkit\.messageHandlers/i,
      /messageHandlers/i,
      /sendDataToNative/i,
      /sendPageHideMessage/i,
    ],
    beforeSend: (event) => (isInjectedNativeBridgeError(event) ? null : event),
    // tracesSampleRate: 1.0,
    // tracePropagationTargets: ["localhost", /^https:\/\/www\.zmbz\.com/],
    // profilesSampleRate: 1.0
  });
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    importProvidersFrom(
      FormsModule,
      BrowserModule,
      AppRoutingModule,
    ),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true },
    provideAnimations(),
  ],
}).catch(err => console.error(err));
